/**
 * TUVI - Demo Configurador 3D (POC)
 * Cocina 1.50M + selector de módulo de reemplazo (microondas) sobre el cajonero.
 * Ruta: http://localhost:3000/demo-3d  (sin login)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF, OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';

const MODELO_URL = '/muebles/cocina_oficial.glb?v=1';

const REEMPLAZOS = {
  estandar: { label: 'Estándar (2 puertas)', url: null },
  B: { label: 'Microondas batiente', url: '/muebles/reemplazos/BSM60B.glb?v=1' },
  E: { label: 'Microondas elevable', url: '/muebles/reemplazos/BSM60E.glb?v=1' },
  A: { label: 'Microondas abierto', url: '/muebles/reemplazos/BSM60A.glb?v=1' },
};

const MAT_CUERPO = ['Roble_Blanco_PELIKANO', 'Niebla_PELIKANO'];
const MAT_FRENTE = ['FrontColor'];
const ANGULO = THREE.MathUtils.degToRad(92);
const ANGULO_ELEV = THREE.MathUtils.degToRad(78);

const esPuertaExplicita = n => /^Puerta_(DERECHA|IZQUIERDA)/i.test(n);
const esGrupoBatiente = n => /Puerta_batiente/i.test(n);
const esPuertaElevable = n => /Puerta_elevable/i.test(n);
const esCajon = n => /Cajon_[A-Z]/.test(n) || /Bloque_1_especiero/i.test(n);
const esModuloAlto = n => /Modulo_alto/i.test(n);
const esDescendiente = (nodo, anc) => { let p = nodo.parent; while (p) { if (p === anc) return true; p = p.parent; } return false; };
const moduloDe = (nodo) => { let p = nodo; while (p) { if (p.name && /Modulo_(alto|bajo|micro)/i.test(p.name)) return p; p = p.parent; } return null; };
const box3 = obj => new THREE.Box3().setFromObject(obj);

const SWATCHES = [
  { nombre: 'Roble', hex: '#c9a06a' }, { nombre: 'Nogal', hex: '#5a3a24' },
  { nombre: 'Blanco', hex: '#eceae5' }, { nombre: 'Negro', hex: '#2b2b2b' }, { nombre: 'Verde', hex: '#3f5e4e' },
];
function setColor(materials, nombres, hex) { nombres.forEach(n => { if (materials[n]) materials[n].color.set(hex); }); }

// --- Módulo de reemplazo: se alinea al hueco y anima su puerta (batiente gira / elevable sube) ---
function Reemplazo({ url, anchor, frontSz, abrir }) {
  const { scene: original } = useGLTF(url);
  const scene = useMemo(() => original.clone(true), [original]); // clon: no ensucia el cache
  const pivotRef = useRef(null);
  const hoverRef = useRef(false);
  const [hitbox, setHitbox] = useState(null);

  useEffect(() => {
    scene.traverse(o => { if (o.isMesh) o.raycast = () => {}; });
    // 1) Alinear al hueco del slot (centro X, tope Y, cara frontal Z)
    scene.position.set(0, 0, 0);
    scene.updateWorldMatrix(true, true);
    const b0 = box3(scene);
    const cx0 = (b0.min.x + b0.max.x) / 2, front0 = frontSz > 0 ? b0.max.z : b0.min.z, top0 = b0.max.y;
    scene.position.set(anchor.cx - cx0, anchor.topY - top0, anchor.frontZ - front0);
    scene.updateWorldMatrix(true, true);

    // 2) Detectar puerta (batiente o elevable)
    let leaf = null, tipo = null;
    scene.traverse(o => {
      if (leaf || !o.name) return;
      if (esGrupoBatiente(o.name)) { (o.children || []).forEach(ch => { if (!leaf) leaf = ch; }); if (leaf) tipo = 'batiente'; }
      else if (esPuertaElevable(o.name)) { (o.children || []).forEach(ch => { if (!leaf) leaf = ch; }); if (leaf) tipo = 'elevable'; }
    });

    pivotRef.current = null;
    if (leaf) {
      // Bisagra en coords LOCALES del módulo (el scene ya tiene offset de alineación)
      scene.updateWorldMatrix(true, true);
      const inv = scene.matrixWorld.clone().invert();
      const modC = box3(scene).applyMatrix4(inv).getCenter(new THREE.Vector3());
      const b = box3(leaf).applyMatrix4(inv); const c = b.getCenter(new THREE.Vector3());
      const hz = frontSz > 0 ? b.max.z : b.min.z;
      const g = new THREE.Group();
      if (tipo === 'batiente') {
        // Usar la posición del nodo "Bisagra" real dentro de la puerta como eje
        let hingeNode = null;
        leaf.traverse(o => { if (!hingeNode && o.name && /Bisagra/i.test(o.name)) hingeNode = o; });
        let hingeX, hingeZ;
        if (hingeNode) {
          const hc = box3(hingeNode).applyMatrix4(inv).getCenter(new THREE.Vector3());
          hingeX = hc.x; hingeZ = hc.z;
        } else {
          const sx = Math.sign(c.x - modC.x) || 1;
          hingeX = sx > 0 ? b.max.x : b.min.x; hingeZ = hz;
        }
        const hingeSide = Math.sign(hingeX - c.x) || 1; // lado donde está la bisagra
        g.position.set(hingeX, c.y, hingeZ);
        g.userData = { axis: 'y', target: hingeSide * (frontSz > 0 ? 1 : -1) * ANGULO };
      } else {
        g.position.set(c.x, b.max.y, hz); // bisagra en arista superior
        g.userData = { axis: 'x', target: (frontSz > 0 ? -1 : 1) * ANGULO_ELEV }; // sube
      }
      scene.add(g); scene.updateWorldMatrix(true, true); g.updateWorldMatrix(true, true); g.attach(leaf);
      pivotRef.current = g;
    }

    // 3) Hitbox que cubre el módulo (para el hover)
    const mb = box3(scene); const ms = mb.getSize(new THREE.Vector3()); const mc = mb.getCenter(new THREE.Vector3());
    setHitbox({ pos: [mc.x, mc.y, mc.z], size: [ms.x * 1.1, ms.y * 1.25, ms.z * 1.8] });
  }, [scene, anchor, frontSz]);

  useFrame(() => {
    const g = pivotRef.current; if (!g) return;
    const open = abrir || hoverRef.current;
    const t = open ? g.userData.target : 0;
    if (g.userData.axis === 'y') g.rotation.y = THREE.MathUtils.lerp(g.rotation.y, t, 0.15);
    else g.rotation.x = THREE.MathUtils.lerp(g.rotation.x, t, 0.15);
  });

  return (
    <group>
      <primitive object={scene} />
      {hitbox && (
        <mesh position={hitbox.pos}
          onPointerOver={e => { e.stopPropagation(); hoverRef.current = true; }}
          onPointerOut={e => { e.stopPropagation(); hoverRef.current = false; }}>
          <boxGeometry args={hitbox.size} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      )}
    </group>
  );
}

function Mueble({ colorCuerpo, colorFrente, abrir, variante, onInfo, onSlot }) {
  const { scene, materials } = useGLTF(MODELO_URL);
  const { camera } = useThree();
  const controls = useThree(s => s.controls);
  const [hits, setHits] = useState([]);
  const openRef = useRef({});
  const pivotsRef = useRef([]);
  const slidesRef = useRef([]);
  const fitRef = useRef(null);
  const slotNodeRef = useRef(null);

  useEffect(() => {
    if (!scene || scene.userData.setup) return;
    const info = {};

    // 1) Limpieza
    const kill = [];
    scene.children.slice(1).forEach(r => kill.push(r));
    scene.traverse(o => { if (o.name && o.name.includes('ThomThom')) kill.push(o); });
    kill.forEach(o => o.removeFromParent());

    scene.updateWorldMatrix(true, true);
    const inv = scene.matrixWorld.clone().invert();
    const sceneCenter = box3(scene).applyMatrix4(inv).getCenter(new THREE.Vector3());

    // 2) SLOT: superior alineado en X con el cajonero
    const dc = [];
    scene.traverse(o => { if (o.name && /Cajon_[A-Z]/.test(o.name)) dc.push(box3(o).getCenter(new THREE.Vector3())); });
    const drawerX = dc.length ? dc.reduce((s, c) => s + c.x, 0) / dc.length : 0;
    const drawerY = dc.length ? dc.reduce((s, c) => s + c.y, 0) / dc.length : 0;
    let slotNode = null, best = Infinity;
    scene.traverse(o => {
      if (!o.name || !esModuloAlto(o.name)) return;
      const c = box3(o).getCenter(new THREE.Vector3());
      if (c.y > drawerY) { const d = Math.abs(c.x - drawerX); if (d < best) { best = d; slotNode = o; } }
    });
    slotNodeRef.current = slotNode;

    // 3) PUERTAS (frente relativo al MÓDULO de cada puerta → superiores/inferiores abren bien)
    const hojas = [];
    scene.traverse(o => {
      if (!o.name) return;
      if (esPuertaExplicita(o.name)) hojas.push(o);
      else if (esGrupoBatiente(o.name)) (o.children || []).forEach(ch => { if (ch.name) hojas.push(ch); });
    });
    const clusters = new Map();
    hojas.forEach(h => { const k = h.parent ? h.parent.uuid : 'root'; if (!clusters.has(k)) clusters.set(k, []); clusters.get(k).push(h); });

    const pivots = [];
    let frontSz = 1;
    clusters.forEach(leaves => {
      const enSlot = !!(slotNode && esDescendiente(leaves[0], slotNode));
      const mod = moduloDe(leaves[0]);
      const modZ = mod ? box3(mod).applyMatrix4(inv).getCenter(new THREE.Vector3()).z : sceneCenter.z;
      const items = leaves.map(mesh => { const b = box3(mesh).applyMatrix4(inv); return { mesh, b, c: b.getCenter(new THREE.Vector3()) }; });
      const pairCX = items.reduce((s, it) => s + it.c.x, 0) / items.length;
      items.forEach(({ mesh, b, c }) => {
        const sx = Math.sign(c.x - pairCX) || 1;
        const hingeX = sx > 0 ? b.max.x : b.min.x;
        const sz = Math.sign(c.z - modZ) || 1;          // frente relativo al módulo
        const hingeZ = sz > 0 ? b.max.z : b.min.z;
        frontSz = sz;
        const g = new THREE.Group();
        g.position.set(hingeX, c.y, hingeZ);
        g.userData.dir = sx * sz; g.userData.id = `puerta_${pivots.length}`; g.userData.enSlot = enSlot;
        scene.add(g); scene.updateWorldMatrix(true, true); g.updateWorldMatrix(true, true); g.attach(mesh);
        pivots.push({ g, enSlot });
      });
    });
    pivotsRef.current = pivots.map(p => p.g);
    info.puertas = pivots.length;

    // 4) CAJONES
    const slides = [];
    const cajonNodes = [];
    scene.traverse(o => { if (o.name && esCajon(o.name)) cajonNodes.push(o); });
    const cajonTop = cajonNodes.filter(n => !cajonNodes.some(m => m !== n && esDescendiente(n, m)));
    cajonTop.forEach((node, i) => {
      const b = box3(node).applyMatrix4(inv);
      const s = b.getSize(new THREE.Vector3());
      const g = new THREE.Group();
      scene.add(g); scene.updateWorldMatrix(true, true); g.updateWorldMatrix(true, true); g.attach(node);
      g.userData.slideZ = s.z * 0.7 * (frontSz || 1); g.userData.id = `cajon_${i}`;
      slides.push(g);
    });
    slidesRef.current = slides;
    info.cajones = slides.length;

    // 5) Recentrar
    scene.updateWorldMatrix(true, true);
    const fullBox = box3(scene);
    const center = fullBox.getCenter(new THREE.Vector3());
    scene.position.sub(center);
    scene.updateWorldMatrix(true, true);

    // 6) Anchor del slot (post-recentrar, coords mundo)
    if (slotNode) {
      const sb = box3(slotNode);
      const anchor = { cx: (sb.min.x + sb.max.x) / 2, topY: sb.max.y, frontZ: frontSz > 0 ? sb.max.z : sb.min.z };
      if (onSlot) onSlot({ anchor, frontSz });
    }

    // 7) Encuadre
    const size = fullBox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const dist = (maxDim / 2) / Math.tan((camera.fov * Math.PI / 180) / 2) * 1.15;
    fitRef.current = { camPos: new THREE.Vector3(dist * 0.45, dist * 0.35, dist * 0.85), maxDim };
    camera.position.copy(fitRef.current.camPos); camera.near = maxDim / 200; camera.far = maxDim * 40;
    camera.lookAt(0, 0, 0); camera.updateProjectionMatrix();

    // 8) Hover por pieza
    scene.traverse(o => { if (o.isMesh) o.raycast = () => {}; });
    const zonas = [];
    pivots.forEach(({ g, enSlot }) => {
      const gb = box3(g); const s = gb.getSize(new THREE.Vector3()); const c = gb.getCenter(new THREE.Vector3());
      const W = s.x; const frontFaceZ = frontSz > 0 ? gb.max.z : gb.min.z;
      zonas.push({ id: g.userData.id, enSlot, size: [W * 1.05, s.y * 1.02, W + s.z], pos: [c.x, c.y, frontFaceZ + ((W - s.z) / 2) * frontSz] });
    });
    slides.forEach(g => {
      const gb = box3(g); const s = gb.getSize(new THREE.Vector3()); const c = gb.getCenter(new THREE.Vector3());
      const slide = g.userData.slideZ;
      zonas.push({ id: g.userData.id, enSlot: false, size: [s.x * 1.02, s.y * 1.02, s.z + Math.abs(slide)], pos: [c.x, c.y, c.z + slide / 2] });
    });
    setHits(zonas);

    scene.userData.setup = true;
    if (onInfo) onInfo(`puertas: ${info.puertas} · cajones: ${info.cajones} · slot: ${slotNode ? 'ok' : 'no'}`);
  }, [scene, camera, onInfo, onSlot]);

  useEffect(() => {
    const f = fitRef.current;
    if (!controls || !f) return;
    camera.position.copy(f.camPos); camera.near = f.maxDim / 200; camera.far = f.maxDim * 40; camera.updateProjectionMatrix();
    controls.target.set(0, 0, 0);
    controls.minDistance = f.maxDim * 0.03; controls.maxDistance = f.maxDim * 6;
    controls.update();
  }, [controls, camera, hits]);

  // Mostrar/ocultar el módulo original del slot (cuerpo + sus puertas) según variante
  useEffect(() => {
    const estandar = variante === 'estandar';
    if (slotNodeRef.current) slotNodeRef.current.visible = estandar;
    pivotsRef.current.forEach((g, i) => { if (g.userData.enSlot) g.visible = estandar; });
  }, [variante, hits]);

  useEffect(() => { setColor(materials, MAT_CUERPO, colorCuerpo); }, [materials, colorCuerpo]);
  useEffect(() => { setColor(materials, MAT_FRENTE, colorFrente); }, [materials, colorFrente]);

  useFrame(() => {
    pivotsRef.current.forEach(g => {
      const open = abrir || !!openRef.current[g.userData.id];
      g.rotation.y = THREE.MathUtils.lerp(g.rotation.y, open ? g.userData.dir * ANGULO : 0, 0.15);
    });
    slidesRef.current.forEach(g => {
      const open = abrir || !!openRef.current[g.userData.id];
      g.position.z = THREE.MathUtils.lerp(g.position.z, open ? g.userData.slideZ : 0, 0.12);
    });
  });

  const hitsVisibles = hits.filter(h => !h.enSlot || variante === 'estandar');

  return (
    <group>
      <primitive object={scene} />
      {hitsVisibles.map(h => (
        <mesh key={h.id} position={h.pos}
          onPointerOver={e => { e.stopPropagation(); openRef.current[h.id] = true; }}
          onPointerOut={e => { e.stopPropagation(); openRef.current[h.id] = false; }}>
          <boxGeometry args={h.size} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

function Escena({ variante, slot, abrir, ...props }) {
  return (
    <Canvas camera={{ position: [2, 1.5, 2.5], fov: 45 }} dpr={[1, 2]}>
      <color attach="background" args={['#f2f1ee']} />
      <hemisphereLight intensity={0.85} groundColor="#b0aca5" />
      <directionalLight position={[3, 5, 2]} intensity={1.5} />
      <directionalLight position={[-3, 2, -2]} intensity={0.5} />
      <React.Suspense fallback={<Html center><span style={{ color: '#555' }}>Cargando cocina…</span></Html>}>
        <Mueble {...props} abrir={abrir} variante={variante} />
        {slot && variante !== 'estandar' && REEMPLAZOS[variante].url && (
          <Reemplazo url={REEMPLAZOS[variante].url} anchor={slot.anchor} frontSz={slot.frontSz} abrir={abrir} />
        )}
      </React.Suspense>
      <OrbitControls makeDefault enableDamping minDistance={0.1} maxDistance={200} />
    </Canvas>
  );
}

export default function Configurador3DDemo() {
  const [colorCuerpo, setColorCuerpo] = useState('#eceae5');
  const [colorFrente, setColorFrente] = useState('#c9a06a');
  const [abrir, setAbrir] = useState(false);
  const [variante, setVariante] = useState('estandar');
  const [slot, setSlot] = useState(null);
  const [dbg, setDbg] = useState('');
  const onInfo = React.useCallback(s => setDbg(s), []);
  const onSlot = React.useCallback(s => setSlot(s), []);

  const panel = { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 };
  const swatchBtn = (hex, active) => ({ width: 26, height: 26, borderRadius: 6, background: hex, cursor: 'pointer', border: active ? '3px solid #111' : '1px solid #ccc' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#f2f1ee' }}>
      <Escena colorCuerpo={colorCuerpo} colorFrente={colorFrente} abrir={abrir} variante={variante} slot={slot} onInfo={onInfo} onSlot={onSlot} />

      <div style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(255,255,255,0.96)', borderRadius: 12, padding: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: 300, fontFamily: 'system-ui, sans-serif', fontSize: 13, color: '#222' }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Cocina 1.50 M</div>
        <div style={{ color: '#666', marginBottom: 12 }}>Hover para abrir · elige el módulo de microondas</div>

        <div style={{ fontWeight: 600 }}>Módulo microondas (sobre el cajonero) ⭐</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 6 }}>
          {Object.entries(REEMPLAZOS).map(([k, v]) => (
            <button key={k} onClick={() => setVariante(k)} style={{
              textAlign: 'left', padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
              border: variante === k ? '2px solid #2563eb' : '1px solid #d1d5db',
              background: variante === k ? '#eff6ff' : '#fff', fontWeight: variante === k ? 700 : 500,
            }}>{v.label}</button>
          ))}
        </div>

        <div style={{ fontWeight: 600, marginTop: 14 }}>Color del cuerpo</div>
        <div style={panel}>{SWATCHES.map(s => <div key={s.nombre} title={s.nombre} style={swatchBtn(s.hex, colorCuerpo === s.hex)} onClick={() => setColorCuerpo(s.hex)} />)}</div>
        <div style={{ fontWeight: 600, marginTop: 12 }}>Color del frente</div>
        <div style={panel}>{SWATCHES.map(s => <div key={s.nombre} title={s.nombre} style={swatchBtn(s.hex, colorFrente === s.hex)} onClick={() => setColorFrente(s.hex)} />)}</div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={abrir} onChange={e => setAbrir(e.target.checked)} /> Abrir todo (ignora el hover)
        </label>
        <div style={{ color: '#888', marginTop: 12, fontSize: 12 }}>Arrastra · rueda zoom · clic derecho desplaza</div>
        {dbg && <div style={{ marginTop: 10, fontSize: 11, color: '#2563eb', fontFamily: 'monospace' }}>🔎 {dbg}</div>}
      </div>
    </div>
  );
}

useGLTF.preload(MODELO_URL);
